import { useCallback, useEffect, useState } from "react";
import { Truck, Plus, Trash2, CheckCircle2, PackagePlus } from "lucide-react";
import { api, ApiClientError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../lib/toast";
import { DataTable, type Column } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";

interface CartonLineDto {
  id: string;
  cartonId: string;
  partNumber: string;
  description: string;
  qty: number;
  uom: string;
  bin: string | null;
}

interface CartonDto {
  id: string;
  vendor: string;
  poNumber: string;
  receivedDate: string;
  status: "Not Started" | "In Progress" | "Received";
  lines: CartonLineDto[];
}

interface DraftLine {
  partNumber: string;
  description: string;
  qty: number;
  uom: string;
}

const EMPTY_DRAFT_LINE: DraftLine = { partNumber: "", description: "", qty: 1, uom: "EA" };

export function Receiving() {
  const { can } = useAuth();
  const { toast } = useToast();

  const [cartons, setCartons] = useState<CartonDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [binDrafts, setBinDrafts] = useState<Record<string, string>>({});

  const [showNewCarton, setShowNewCarton] = useState(false);
  const [vendor, setVendor] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [draftLines, setDraftLines] = useState<DraftLine[]>([]);
  const [currentLine, setCurrentLine] = useState<DraftLine>(EMPTY_DRAFT_LINE);

  const canReceive = can("receiving.receive");

  const load = useCallback(async () => {
    setLoading(true);
    const rows = await api.get<CartonDto[]>("/cartons");
    setCartons(rows);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = cartons.find((c) => c.id === selectedId) ?? null;

  function addDraftLine() {
    if (!currentLine.partNumber.trim() || currentLine.qty <= 0) return;
    setDraftLines((prev) => [...prev, currentLine]);
    setCurrentLine(EMPTY_DRAFT_LINE);
  }
  function removeDraftLine(index: number) {
    setDraftLines((prev) => prev.filter((_, i) => i !== index));
  }

  async function submitNewCarton() {
    if (!vendor.trim() || !poNumber.trim() || draftLines.length === 0) return;
    setBusy(true);
    try {
      await api.post("/cartons", { vendor: vendor.trim(), poNumber: poNumber.trim(), lines: draftLines });
      toast("Carton created.", "success");
      setVendor("");
      setPoNumber("");
      setDraftLines([]);
      setCurrentLine(EMPTY_DRAFT_LINE);
      setShowNewCarton(false);
      await load();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to create carton.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function assignBin(cartonId: string, lineId: string) {
    const bin = (binDrafts[lineId] ?? "").trim();
    if (!bin) return;
    setBusy(true);
    try {
      await api.patch(`/cartons/${cartonId}/lines/${lineId}/bin`, { bin });
      toast(`Bin ${bin} assigned — stock received.`, "success");
      setBinDrafts((prev) => {
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
      await load();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to assign bin.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function completeCarton(cartonId: string) {
    setBusy(true);
    try {
      await api.post(`/cartons/${cartonId}/complete`);
      toast("Carton received — stock updated in inventory.", "success");
      await load();
    } catch (err) {
      toast(err instanceof ApiClientError ? err.message : "Unable to complete carton.", "error");
    } finally {
      setBusy(false);
    }
  }

  const columns: Column<CartonDto>[] = [
    { key: "id", header: "Carton / PO", render: (c) => (
      <div>
        <span className="font-bold text-brand-blue">{c.poNumber}</span>
        <div className="text-[11px] text-ink-muted">{c.id}</div>
      </div>
    ) },
    { key: "vendor", header: "Vendor", render: (c) => c.vendor },
    { key: "receivedDate", header: "Received Date", render: (c) => c.receivedDate.slice(0, 10) },
    { key: "status", header: "Status", render: (c) => <StatusBadge status={c.status} /> },
    { key: "lines", header: "Lines", render: (c) => c.lines.length },
  ];

  const allBinned = selected ? selected.lines.length > 0 && selected.lines.every((l) => !!l.bin) : false;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3.5">
            <h2 className="text-sm font-bold uppercase tracking-wide text-ink-muted flex items-center gap-2">
              <Truck size={16} /> Inbound Cartons
            </h2>
            {canReceive && (
              <button className="btn-primary" onClick={() => setShowNewCarton((v) => !v)}>
                <PackagePlus size={15} /> New Carton
              </button>
            )}
          </div>

          {showNewCarton && (
            <div className="border border-border rounded-md p-3.5 mb-3.5 flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="field-label">Vendor</label>
                  <input className="input" value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="Vendor name" />
                </div>
                <div>
                  <label className="field-label">PO Number</label>
                  <input className="input" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="PO-10234" />
                </div>
              </div>

              {draftLines.length > 0 && (
                <div className="flex flex-col gap-1.5">
                  {draftLines.map((l, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-1">
                      <span>
                        {l.partNumber} — {l.description || "—"} ({l.qty} {l.uom})
                      </span>
                      <button aria-label="Remove line" className="text-ink-muted hover:text-status-red" onClick={() => removeDraftLine(i)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
                <input className="input col-span-2 sm:col-span-1" placeholder="Part #" value={currentLine.partNumber} onChange={(e) => setCurrentLine((c) => ({ ...c, partNumber: e.target.value }))} />
                <input className="input col-span-2" placeholder="Description" value={currentLine.description} onChange={(e) => setCurrentLine((c) => ({ ...c, description: e.target.value }))} />
                <input type="number" min={1} className="input col-span-2 sm:col-span-1" placeholder="Qty" value={currentLine.qty} onChange={(e) => setCurrentLine((c) => ({ ...c, qty: Number(e.target.value) || 1 }))} />
                <input className="input col-span-2 sm:col-span-1" placeholder="UOM" value={currentLine.uom} onChange={(e) => setCurrentLine((c) => ({ ...c, uom: e.target.value || "EA" }))} />
              </div>
              <button className="btn-outline justify-center" disabled={!currentLine.partNumber.trim()} onClick={addDraftLine}>
                <Plus size={15} /> Add Line
              </button>

              <button
                className="btn-primary justify-center"
                disabled={busy || !vendor.trim() || !poNumber.trim() || draftLines.length === 0}
                onClick={() => void submitNewCarton()}
              >
                {busy ? "Creating…" : "Create Carton"}
              </button>
            </div>
          )}

          <DataTable
            columns={columns}
            rows={cartons}
            rowKey={(c) => c.id}
            onRowClick={(c) => setSelectedId(c.id)}
            emptyMessage={loading ? "Loading cartons…" : "No cartons received yet."}
          />
        </div>

        <div className="card p-5 h-fit sticky top-20">
          {!selected ? (
            <div className="text-sm text-ink-muted text-center py-10">Select a carton to view its lines.</div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-3.5">
                <div>
                  <div className="text-lg font-extrabold">{selected.poNumber}</div>
                  <div className="text-sm text-ink-muted">{selected.vendor} · {selected.id}</div>
                </div>
                <StatusBadge status={selected.status} />
              </div>

              <div className="flex flex-col gap-2 mb-4">
                {selected.lines.map((line) => (
                  <div key={line.id} className="border border-border rounded-md px-3 py-2.5">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-semibold">{line.partNumber}</span>
                      <span className="text-ink-muted">{line.qty} {line.uom}</span>
                    </div>
                    <div className="text-xs text-ink-muted mb-2">{line.description}</div>
                    {line.bin ? (
                      <div className="text-xs font-bold text-status-green">Bin {line.bin} — received</div>
                    ) : canReceive ? (
                      <div className="flex gap-2">
                        <input
                          className="input"
                          placeholder="Bin location"
                          value={binDrafts[line.id] ?? ""}
                          onChange={(e) => setBinDrafts((prev) => ({ ...prev, [line.id]: e.target.value }))}
                        />
                        <button
                          className="btn-outline"
                          disabled={busy || !(binDrafts[line.id] ?? "").trim()}
                          onClick={() => void assignBin(selected.id, line.id)}
                        >
                          Assign
                        </button>
                      </div>
                    ) : (
                      <div className="text-xs text-ink-muted">No bin assigned yet.</div>
                    )}
                  </div>
                ))}
              </div>

              {canReceive && selected.status !== "Received" && (
                <button
                  className="btn-primary w-full justify-center"
                  disabled={busy || !allBinned}
                  title={!allBinned ? "Every line must have a bin assigned first" : undefined}
                  onClick={() => void completeCarton(selected.id)}
                >
                  <CheckCircle2 size={16} /> Complete Carton
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
